/**
 * Designs saved in this browser. A record holds the project file plus what the file does not
 * carry (name, target settings, a thumbnail); the source photo lives in a second store so that
 * listing the library stays light.
 */
export interface DesignRecord {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /** Compact project JSON: configuration, pins, sequence and render settings. */
  project: string;
  resolution: number;
  cropMode: 'square' | 'fit';
  sourceName: string;
  width: number;
  height: number;
  strings: number;
  pins: number;
  /** Data URL of the rendered strings, or null where nothing could be rendered. */
  thumb: string | null;
}

export interface DesignLibrary {
  /** Every design, most recently edited first. */
  list(): Promise<DesignRecord[]>;
  get(id: string): Promise<DesignRecord | null>;
  put(record: DesignRecord): Promise<void>;
  /** Removes the record and its source photo. */
  remove(id: string): Promise<void>;
  getSource(id: string): Promise<Blob | null>;
  putSource(id: string, blob: Blob): Promise<void>;
}

export const byRecent = (a: DesignRecord, b: DesignRecord): number => b.updatedAt - a.updatedAt;

const DESIGNS = 'designs';
const SOURCES = 'sources';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** The library backed by IndexedDB; the database opens on first use. */
export function openLibrary(name = 'stringcanvas'): DesignLibrary {
  let db: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> =>
    (db ??= new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available in this browser.'));
        return;
      }
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => {
        const d = req.result;
        if (!d.objectStoreNames.contains(DESIGNS)) d.createObjectStore(DESIGNS, { keyPath: 'id' });
        if (!d.objectStoreNames.contains(SOURCES)) d.createObjectStore(SOURCES);
      };
      req.onsuccess = () => {
        const d = req.result;
        d.onversionchange = () => {
          d.close();
          db = null;
        };
        resolve(d);
      };
      req.onerror = () => reject(req.error ?? new Error('Could not open the design library.'));
      req.onblocked = () => reject(new Error('Another tab is holding the design library open.'));
    }));
  const transaction = async (stores: string | string[], mode: IDBTransactionMode): Promise<IDBTransaction> => (await open()).transaction(stores, mode);

  return {
    async list() {
      const tx = await transaction(DESIGNS, 'readonly');
      const all = await request(tx.objectStore(DESIGNS).getAll() as IDBRequest<DesignRecord[]>);
      return all.sort(byRecent);
    },
    async get(id) {
      const tx = await transaction(DESIGNS, 'readonly');
      return (await request(tx.objectStore(DESIGNS).get(id) as IDBRequest<DesignRecord | undefined>)) ?? null;
    },
    async put(record) {
      const tx = await transaction(DESIGNS, 'readwrite');
      tx.objectStore(DESIGNS).put(record);
      await done(tx);
    },
    async remove(id) {
      const tx = await transaction([DESIGNS, SOURCES], 'readwrite');
      tx.objectStore(DESIGNS).delete(id);
      tx.objectStore(SOURCES).delete(id);
      await done(tx);
    },
    async getSource(id) {
      const tx = await transaction(SOURCES, 'readonly');
      return (await request(tx.objectStore(SOURCES).get(id) as IDBRequest<Blob | undefined>)) ?? null;
    },
    async putSource(id, blob) {
      const tx = await transaction(SOURCES, 'readwrite');
      tx.objectStore(SOURCES).put(blob, id);
      await done(tx);
    },
  };
}

/** An in-memory library, for tests and for browsers without IndexedDB. */
export function memoryLibrary(): DesignLibrary {
  const designs = new Map<string, DesignRecord>();
  const sources = new Map<string, Blob>();
  return {
    async list() {
      return [...designs.values()].sort(byRecent);
    },
    async get(id) {
      return designs.get(id) ?? null;
    },
    async put(record) {
      designs.set(record.id, { ...record });
    },
    async remove(id) {
      designs.delete(id);
      sources.delete(id);
    },
    async getSource(id) {
      return sources.get(id) ?? null;
    },
    async putSource(id, blob) {
      sources.set(id, blob);
    },
  };
}
